/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, DatePicker, Empty, Form, Input, InputNumber, Modal, Space, Spin, Statistic, Table, Tag, Typography, message } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { datePickerFormat, formatDate } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import { useGetPlantingsQuery } from '../../api/plantingsApi';
import { useCreateHarvestRecordMutation, useGetHarvestRecordsQuery } from '../../api/harvestApi';
import { useGetSaveRuntimeSessionQuery, useListSimEventsQuery } from '../../api/simulationApi';
import simulationApi from '../../api/simulationApi';
import type { Planting } from '../../models/Planting';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import { useSave } from '../../contexts/WorldContext';
import InventoryLedgerSection from '../../components/inventory/InventoryLedgerSection';
import LiveAssetsSection from '../../components/inventory/LiveAssetsSection';

const { Title, Text } = Typography;

type SimHarvestVolume = {
  collectedKg: number;
  lostKg: number;
  availableKg: number;
  projectedKg: number;
  eventCount: number;
};

const getActiveStorageLocation = (planting: Planting) =>
  planting.locations.find((loc) => !loc.toDate && (loc.saveStorageId || loc.farmStorage));

const FarmStoragePage: React.FC = () => {
  const { t } = useTranslation('inventory');
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const farmId = save.farmId;
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [form] = Form.useForm();
  const [isHarvestModalOpen, setHarvestModalOpen] = useState(false);

  const {
    data: plantings = [],
    isLoading,
    isError,
    refetch,
  } = useGetPlantingsQuery(
    saveId
      ? {
          saveId,
          lifecycleStatus: 'holding',
          limit: 1000,
          offset: 0,
        }
      : undefined,
    { skip: !saveId }
  );

  const { data: allPlantings = [] } = useGetPlantingsQuery(
    saveId
      ? {
          saveId,
          limit: 1000,
          offset: 0,
        }
      : undefined,
    { skip: !saveId }
  );

  const storagePlantings = useMemo(
    () =>
      plantings
        .filter((p) => Boolean(getActiveStorageLocation(p)))
        .sort((a, b) => a.crop.name.localeCompare(b.crop.name, 'ru')),
    [plantings]
  );

  const { data: activeSimSession } = useGetSaveRuntimeSessionQuery(saveId ?? '', { skip: !saveId || !isAuthenticated });
  const activeSimSessionId = activeSimSession?.id;
  const activeSimSaveId = activeSimSession?.saveId || null;
  const sessionBadgeColor = activeSimSession ? 'green' : 'warning';
  const sessionBadgeLabel = activeSimSession ? t('storage.simActive') : t('storage.simUnavailable');
  const [fetchSimulationTimeseries] = simulationApi.endpoints.getSimulationTimeseries.useLazyQuery();

  const { data: simEventsData, isLoading: isSimEventsLoading } = useListSimEventsQuery(
    { saveId: activeSimSaveId || '', sessionId: activeSimSessionId },
    { skip: !activeSimSaveId }
  );

  const {
    data: harvestData,
    isLoading: isHarvestLoading,
    isError: isHarvestError,
    refetch: refetchHarvest,
  } = useGetHarvestRecordsQuery(
    farmId ? { farmId, limit: 100, offset: 0 } : (undefined as any),
    { skip: !farmId }
  );

  const simHarvestEvents = useMemo(() => {
    const events = simEventsData?.events || [];
    return events
      .filter((evt: any) => {
        const action = String(evt?.action || '').toLowerCase();
        return action === 'harvest' || action === 'harvest_collection';
      })
      .sort((a: any, b: any) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [simEventsData?.events]);

  const simTransferEvents = useMemo(() => {
    const events = simEventsData?.events || [];
    return events
      .filter((evt: any) => String(evt?.action || '').toLowerCase() === 'transplant')
      .sort((a: any, b: any) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [simEventsData?.events]);

  const plantingsById = useMemo(() => {
    const map = new Map<string, Planting>();
    allPlantings.forEach((p) => map.set(String(p.id), p));
    plantings.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [allPlantings, plantings]);

  const uniqueSimHarvestPlantings = useMemo(() => {
    const set = new Set<string>();
    simHarvestEvents.forEach((evt: any) => {
      const plantingId = String(evt?.plantingId || evt?.planting_id || '');
      if (plantingId) set.add(plantingId);
    });
    return set.size;
  }, [simHarvestEvents]);

  const simHarvestPlantingIds = useMemo(() => {
    const set = new Set<string>();
    simHarvestEvents.forEach((evt: any) => {
      const plantingId = String(evt?.plantingId || evt?.planting_id || '');
      if (plantingId) set.add(plantingId);
    });
    return Array.from(set);
  }, [simHarvestEvents]);

  const simHarvestEventIds = useMemo(() => {
    return new Set(simHarvestEvents.map((evt: any) => String(evt?.id || '')).filter(Boolean));
  }, [simHarvestEvents]);

  const simHarvestRecordsByPlanting = useMemo(() => {
    const grouped: Record<string, { collectedKg: number; recordCount: number }> = {};
    (harvestData?.records || []).forEach((record: any) => {
      const eventId = String(record?.eventId || '');
      const plantingId = String(record?.plantingId || '');
      if (!eventId || !plantingId || !simHarvestEventIds.has(eventId)) return;
      const quantityKg = Number(record?.quantityKg || 0);
      if (!Number.isFinite(quantityKg) || quantityKg <= 0) return;
      grouped[plantingId] = grouped[plantingId] || { collectedKg: 0, recordCount: 0 };
      grouped[plantingId].collectedKg += quantityKg;
      grouped[plantingId].recordCount += 1;
    });
    return grouped;
  }, [harvestData?.records, simHarvestEventIds]);

  const [simHarvestVolumesByPlanting, setSimHarvestVolumesByPlanting] = useState<Record<string, SimHarvestVolume>>({});
  const [isSimHarvestVolumesLoading, setSimHarvestVolumesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadVolumes = async () => {
      if (!activeSimSessionId || simHarvestPlantingIds.length === 0) {
        setSimHarvestVolumesByPlanting({});
        setSimHarvestVolumesLoading(false);
        return;
      }

      setSimHarvestVolumesLoading(true);

      const eventCountByPlanting: Record<string, number> = {};
      simHarvestEvents.forEach((evt: any) => {
        const plantingId = String(evt?.plantingId || evt?.planting_id || '');
        if (!plantingId) return;
        eventCountByPlanting[plantingId] = (eventCountByPlanting[plantingId] || 0) + 1;
      });

      const entries = await Promise.all(
        simHarvestPlantingIds.map(async (plantingId) => {
          try {
            const response = await fetchSimulationTimeseries(
              { sessionId: activeSimSessionId, plantingId },
              true
            ).unwrap();

            const planting = plantingsById.get(plantingId);
            const quantityMultiplier = planting?.plantingType === 'batch'
              ? Math.max(1, Number(planting?.quantity || 1))
              : 1;
            const finalState: any = response?.finalState || {};
            const harvestForecast: any = finalState.harvestForecast || {};
            const lastPoint: any = response?.timeSeries?.length
              ? response.timeSeries[response.timeSeries.length - 1]
              : {};
            const recordVolume = simHarvestRecordsByPlanting[plantingId];

            const collectedFromStateKg = Number(
              harvestForecast.collectedCumulativeKg ?? finalState.cumulativeHarvestCollectedKg ?? lastPoint.cumulativeHarvestCollectedKg ?? 0
            ) * quantityMultiplier;
            const collectedKg = recordVolume?.collectedKg ?? collectedFromStateKg;
            const lostKg = Number(
              harvestForecast.lostCumulativeKg ?? finalState.cumulativeHarvestLostKg ?? lastPoint.cumulativeHarvestLostKg ?? 0
            ) * quantityMultiplier;
            const availableKg = Number(
              harvestForecast.availableKg ?? finalState.harvestAvailableKg ?? lastPoint.harvestAvailableKg ?? 0
            ) * quantityMultiplier;
            const projectedFromStateKg = Number(
              harvestForecast.projectedTotalKg ?? finalState.harvestProjectedTotalKg ?? lastPoint.harvestProjectedTotalKg ?? 0
            ) * quantityMultiplier;
            const projectedKg = Math.max(recordVolume?.collectedKg ?? 0, projectedFromStateKg);

            return [
              plantingId,
              {
                collectedKg,
                lostKg,
                availableKg,
                projectedKg,
                eventCount: recordVolume?.recordCount || eventCountByPlanting[plantingId] || 0,
              } satisfies SimHarvestVolume,
            ] as const;
          } catch {
            return [
              plantingId,
              {
                collectedKg: 0,
                lostKg: 0,
                availableKg: 0,
                projectedKg: 0,
                eventCount: eventCountByPlanting[plantingId] || 0,
              } satisfies SimHarvestVolume,
            ] as const;
          }
        })
      );

      if (!cancelled) {
        setSimHarvestVolumesByPlanting(Object.fromEntries(entries));
        setSimHarvestVolumesLoading(false);
      }
    };

    loadVolumes();

    return () => {
      cancelled = true;
    };
  }, [activeSimSessionId, fetchSimulationTimeseries, plantingsById, simHarvestEvents, simHarvestPlantingIds, simHarvestRecordsByPlanting]);

  const simHarvestPlantingRows = useMemo(() => {
    return simHarvestPlantingIds
      .map((plantingId) => {
        const planting = plantingsById.get(plantingId);
        const volume = simHarvestVolumesByPlanting[plantingId] || {
          collectedKg: 0,
          lostKg: 0,
          availableKg: 0,
          projectedKg: 0,
          eventCount: 0,
        };

        return {
          key: plantingId,
          plantingId,
          cropName: planting?.crop?.name || t('storage.unknownCrop'),
          cropLatinName: planting?.crop?.latinName || '',
          plantingTypeLabel: planting
            ? (planting.plantingType === 'batch' ? t('storage.batch', { count: planting.quantity ?? 1 }) : t('storage.single'))
            : '—',
          ...volume,
        };
      })
      .sort((a, b) => b.collectedKg - a.collectedKg);
  }, [plantingsById, simHarvestPlantingIds, simHarvestVolumesByPlanting, t]);

  const simHarvestByCropRows = useMemo(() => {
    const grouped = new Map<string, {
      key: string;
      cropName: string;
      cropLatinName: string;
      plantingsCount: number;
      harvestEventsCount: number;
      collectedKg: number;
      lostKg: number;
      availableKg: number;
      projectedKg: number;
    }>();

    simHarvestPlantingRows.forEach((row) => {
      const key = `${row.cropName}::${row.cropLatinName}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          cropName: row.cropName,
          cropLatinName: row.cropLatinName,
          plantingsCount: 0,
          harvestEventsCount: 0,
          collectedKg: 0,
          lostKg: 0,
          availableKg: 0,
          projectedKg: 0,
        });
      }
      const item = grouped.get(key)!;
      item.plantingsCount += 1;
      item.harvestEventsCount += row.eventCount;
      item.collectedKg += row.collectedKg;
      item.lostKg += row.lostKg;
      item.availableKg += row.availableKg;
      item.projectedKg += row.projectedKg;
    });

    return Array.from(grouped.values()).sort((a, b) => b.collectedKg - a.collectedKg);
  }, [simHarvestPlantingRows]);

  const simHarvestTotals = useMemo(() => {
    return simHarvestPlantingRows.reduce(
      (acc, row) => {
        acc.collectedKg += row.collectedKg;
        acc.lostKg += row.lostKg;
        acc.availableKg += row.availableKg;
        return acc;
      },
      { collectedKg: 0, lostKg: 0, availableKg: 0 }
    );
  }, [simHarvestPlantingRows]);

  const [createHarvestRecord, { isLoading: isCreatingHarvest }] = useCreateHarvestRecordMutation();

  const handleOpenHarvestModal = () => {
    form.resetFields();
    form.setFieldsValue({
      harvestedAt: dayjs(),
      quantityKg: 1,
    });
    setHarvestModalOpen(true);
  };

  const handleCreateHarvest = async () => {
    if (!farmId) return;
    try {
      const values = await form.validateFields();
      const selectedPlanting = storagePlantings.find((p) => String(p.id) === String(values.plantingId));
      await createHarvestRecord({
        farmId,
        plantingId: values.plantingId || undefined,
        cropId: selectedPlanting?.crop?.id != null ? String(selectedPlanting.crop.id) : undefined,
        harvestedAt: values.harvestedAt?.format('YYYY-MM-DD'),
        quantityKg: Number(values.quantityKg),
        marketableKg: values.marketableKg != null ? Number(values.marketableKg) : undefined,
        notes: values.notes || undefined,
      }).unwrap();
      message.success(t('storage.harvestRecordAdded'));
      setHarvestModalOpen(false);
      refetchHarvest();
    } catch (err: any) {
      if (err?.data?.message) {
        message.error(err.data.message);
      }
    }
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('storage.breadcrumbFarms')}</Link> },
            { title: <Link to={`/saves/${saveId}`}>{t('storage.breadcrumbFarm')}</Link> },
            { title: t('storage.breadcrumbStorage') },
          ]}
        />
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={4}>
            <Title level={4} style={{ margin: 0 }}>
              {t('storage.title')}
            </Title>
            <Text type="secondary">{t('storage.subtitle')}</Text>
          </Space>
        </Card>

        {saveId && (
          <InventoryLedgerSection
            saveId={saveId}
            saveType={save.type}
            cursorDate={save.cursor?.simDate || save.cursor?.sim_date || null}
          />
        )}

        {saveId && <LiveAssetsSection saveId={saveId} />}

        <Card
          title={t('storage.inStorage', { count: storagePlantings.length })}
          extra={<Button onClick={() => refetch()}>{t('common:actions.refresh')}</Button>}
        >
          {isLoading ? (
            <Spin tip={t('storage.loadingPlantings')} />
          ) : isError ? (
            <Card type="inner">{t('storage.plantingsLoadFailed')}</Card>
          ) : storagePlantings.length === 0 ? (
            <Empty description={t('storage.empty')} />
          ) : (
            <Table
              rowKey={(row) => String(row.id)}
              pagination={false}
              dataSource={storagePlantings}
              columns={[
                {
                  title: t('liveAssets.crop'),
                  key: 'crop',
                  render: (_: unknown, row: Planting) => (
                    <Space direction="vertical" size={0}>
                      <Link to={`/saves/${saveId}/plantings/${row.id}`}>{row.crop.name}</Link>
                      {row.crop.latinName ? (
                        <Text type="secondary" italic>
                          {row.crop.latinName}
                        </Text>
                      ) : null}
                    </Space>
                  ),
                },
                {
                  title: t('common:labels.type'),
                  key: 'type',
                  render: (_: unknown, row: Planting) => (
                    <Tag color={row.plantingType === 'batch' ? 'purple' : 'blue'}>
                      {row.plantingType === 'batch' ? t('storage.batch', { count: row.quantity ?? 1 }) : t('storage.single')}
                    </Tag>
                  ),
                },
                {
                  title: t('storage.placedAt'),
                  key: 'fromDate',
                  render: (_: unknown, row: Planting) => {
                    const loc = getActiveStorageLocation(row);
                    return loc?.fromDate ? formatDate(loc.fromDate.slice(0, 10), 'short') : '—';
                  },
                },
              ]}
            />
          )}
        </Card>

        <Card
          title={t('storage.harvestLog')}
          extra={
            <Space>
              <Button onClick={() => refetchHarvest()}>{t('common:actions.refresh')}</Button>
              <Button type="primary" onClick={handleOpenHarvestModal}>{t('storage.addRecord')}</Button>
            </Space>
          }
        >
          <Space size={24} style={{ marginBottom: 16 }}>
            <Statistic title={t('storage.records')} value={harvestData?.summary?.recordsCount ?? 0} />
            <Statistic title={t('storage.totalHarvested')} value={harvestData?.summary?.totalQuantityKg ?? 0} precision={2} suffix={t('storage.kg')} />
            <Statistic title={t('storage.marketableYield')} value={harvestData?.summary?.totalMarketableKg ?? 0} precision={2} suffix={t('storage.kg')} />
          </Space>

          {isHarvestLoading ? (
            <Spin tip={t('storage.loadingHarvestLog')} />
          ) : isHarvestError ? (
            <Card type="inner">{t('storage.harvestLogLoadFailed')}</Card>
          ) : !harvestData?.records?.length ? (
            <Empty description={t('storage.noHarvestRecords')} />
          ) : (
            <Table
              rowKey={(row) => String(row.id)}
              pagination={false}
              dataSource={harvestData.records}
              columns={[
                {
                  title: t('common:labels.date'),
                  dataIndex: 'harvestedAt',
                  render: (d: string | null) => (d ? formatDate(String(d).slice(0, 10), 'short') : '—'),
                },
                {
                  title: t('liveAssets.crop'),
                  key: 'crop',
                  render: (_: unknown, row: any) => (
                    <Space direction="vertical" size={0}>
                      <Text>{row.cropName || '—'}</Text>
                      {row.cropLatinName ? (
                        <Text type="secondary" italic>{row.cropLatinName}</Text>
                      ) : null}
                    </Space>
                  ),
                },
                {
                  title: t('storage.quantity'),
                  key: 'qty',
                  render: (_: unknown, row: any) => t('units.kgValue', { value: Number(row.quantityKg).toFixed(2) }),
                },
                {
                  title: t('storage.marketable'),
                  key: 'marketable',
                  render: (_: unknown, row: any) => row.marketableKg == null ? '—' : t('units.kgValue', { value: Number(row.marketableKg).toFixed(2) }),
                },
                {
                  title: t('storage.sourcePlanting'),
                  key: 'planting',
                  render: (_: unknown, row: any) => row.plantingId ? <Text code>{row.plantingId}</Text> : '—',
                },
              ]}
            />
          )}
        </Card>

        <Card
          title={t('storage.simHarvestTitle')}
          extra={<Tag color={sessionBadgeColor}>{sessionBadgeLabel}</Tag>}
        >
          <Space size={24} style={{ marginBottom: 16 }}>
            <Statistic title={t('storage.harvestEvents')} value={simHarvestEvents.length} />
            <Statistic title={t('storage.affectedPlantings')} value={uniqueSimHarvestPlantings} />
            <Statistic title={t('storage.collectedByEvents')} value={simHarvestTotals.collectedKg} precision={2} suffix={t('storage.kg')} />
            <Statistic title={t('storage.availableNow')} value={simHarvestTotals.availableKg} precision={2} suffix={t('storage.kg')} />
          </Space>

          {!activeSimSessionId ? (
            <Empty description={t('storage.sessionUnavailable')} />
          ) : isSimEventsLoading ? (
            <Spin tip={t('storage.loadingSimHarvests')} />
          ) : isSimHarvestVolumesLoading ? (
            <Spin tip={t('storage.computingVolumes')} />
          ) : simHarvestEvents.length === 0 ? (
            <Empty description={t('storage.noSimHarvests')} />
          ) : (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <Table
                rowKey={(row: any) => String(row.key)}
                pagination={false}
                dataSource={simHarvestByCropRows}
                columns={[
                  {
                    title: t('liveAssets.crop'),
                    key: 'crop',
                    render: (_: unknown, row: any) => (
                      <Space direction="vertical" size={0}>
                        <Text strong>{row.cropName}</Text>
                        {row.cropLatinName ? <Text type="secondary" italic>{row.cropLatinName}</Text> : null}
                      </Space>
                    ),
                  },
                  {
                    title: t('storage.plantingsCount'),
                    dataIndex: 'plantingsCount',
                    width: 100,
                  },
                  {
                    title: t('storage.harvestsCount'),
                    dataIndex: 'harvestEventsCount',
                    width: 100,
                  },
                  {
                    title: t('storage.collectedKg'),
                    key: 'collectedKg',
                    render: (_: unknown, row: any) => Number(row.collectedKg).toFixed(2),
                  },
                  {
                    title: t('storage.lostKg'),
                    key: 'lostKg',
                    render: (_: unknown, row: any) => Number(row.lostKg).toFixed(2),
                  },
                  {
                    title: t('storage.availableKg'),
                    key: 'availableKg',
                    render: (_: unknown, row: any) => Number(row.availableKg).toFixed(2),
                  },
                ]}
              />

              <Table
                rowKey={(row: any) => String(row.key)}
                pagination={false}
                dataSource={simHarvestPlantingRows}
                columns={[
                  {
                    title: t('liveAssets.plantingFallback'),
                    key: 'planting',
                    render: (_: unknown, row: any) => (
                      <Space direction="vertical" size={0}>
                        <Link to={`/saves/${saveId}/plantings/${row.plantingId}`}>{row.cropName}</Link>
                        <Text type="secondary" css={{ fontSize: 12 }}>
                          {row.plantingTypeLabel}
                        </Text>
                        <Text code>{row.plantingId}</Text>
                      </Space>
                    ),
                  },
                  {
                    title: t('storage.harvestEventsCount'),
                    dataIndex: 'eventCount',
                    width: 130,
                  },
                  {
                    title: t('storage.collectedKg'),
                    key: 'collectedKg',
                    render: (_: unknown, row: any) => Number(row.collectedKg).toFixed(2),
                  },
                  {
                    title: t('storage.lostKg'),
                    key: 'lostKg',
                    render: (_: unknown, row: any) => Number(row.lostKg).toFixed(2),
                  },
                  {
                    title: t('storage.availableKg'),
                    key: 'availableKg',
                    render: (_: unknown, row: any) => Number(row.availableKg).toFixed(2),
                  },
                  {
                    title: t('storage.projectedKg'),
                    key: 'projectedKg',
                    render: (_: unknown, row: any) => Number(row.projectedKg).toFixed(2),
                  },
                ]}
              />
            </Space>
          )}

        </Card>

        <Card
          title={t('storage.simTransfersTitle')}
          extra={<Tag color={sessionBadgeColor}>{sessionBadgeLabel}</Tag>}
        >
          {!activeSimSessionId ? (
            <Empty description={t('storage.sessionUnavailable')} />
          ) : isSimEventsLoading ? (
            <Spin tip={t('storage.loadingTransfers')} />
          ) : simTransferEvents.length === 0 ? (
            <Empty description={t('storage.noSimTransfers')} />
          ) : (
            <Table
              rowKey={(row: any) => String(row.id)}
              pagination={false}
              dataSource={simTransferEvents}
              columns={[
                {
                  title: t('common:labels.date'),
                  dataIndex: 'date',
                  render: (d: string | null) => (d ? formatDate(String(d).slice(0, 10), 'short') : '—'),
                  width: 120,
                },
                {
                  title: t('liveAssets.plantingFallback'),
                  key: 'planting',
                  render: (_: unknown, row: any) => {
                    const p = plantingsById.get(String(row.planting_id));
                    return p ? (
                      <Link to={`/saves/${saveId}/plantings/${p.id}`}>{p.crop.name}</Link>
                    ) : (
                      <Text code>{row.planting_id || '—'}</Text>
                    );
                  },
                },
                {
                  title: t('common:labels.status'),
                  dataIndex: 'status',
                  width: 120,
                  render: (val: string) => <Tag>{val}</Tag>,
                },
              ]}
            />
          )}
        </Card>
      </Space>

      <Modal
        title={t('storage.addHarvestRecord')}
        open={isHarvestModalOpen}
        onCancel={() => setHarvestModalOpen(false)}
        onOk={handleCreateHarvest}
        confirmLoading={isCreatingHarvest}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="harvestedAt" label={t('storage.harvestDate')} rules={[{ required: true, message: t('storage.dateRequired') }]}>
            <DatePicker format={datePickerFormat()} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="plantingId" label={t('storage.sourcePlantingOptional')}>
            <Input placeholder={t('storage.plantingUuid')} />
          </Form.Item>
          <Form.Item name="quantityKg" label={t('storage.totalVolumeKg')} rules={[{ required: true, message: t('storage.volumeRequired') }]}>
            <InputNumber style={{ width: '100%' }} min={0.001} step={0.1} />
          </Form.Item>
          <Form.Item name="marketableKg" label={t('storage.marketableVolumeKgOptional')}>
            <InputNumber style={{ width: '100%' }} min={0} step={0.1} />
          </Form.Item>
          <Form.Item name="notes" label={t('storage.comment')}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default FarmStoragePage;
